import React from 'react';
import { InfoIcon } from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  href: string;
}

export interface SiteSetupDeviceMin {
  secondaryDevice?: {
    speakers?: unknown[];
    relays?: unknown[];
    activationWkt?: string | null;
    integrationType?: string;
  };
}

interface SiteSetupProps {
  siteName?: string;
  loadingSite: boolean;
  armStatusLoading: boolean;
  loadingDevices: boolean;
  loadingContacts: boolean;
  navItems: NavItem[];
  getSectionState: (id: string) => string;
  getSectionMessage: (id: string) => string;
  renderStatusIcon: (status: string) => React.ReactNode;
  companyId?: string;
  devices: SiteSetupDeviceMin[];
}

export function SiteSetup(props: SiteSetupProps) {
  const {
    siteName,
    loadingSite,
    armStatusLoading,
    loadingDevices,
    loadingContacts,
    navItems,
    getSectionState,
    getSectionMessage,
    renderStatusIcon,
    companyId,
    devices,
  } = props;

  if (loadingSite || armStatusLoading || loadingDevices || loadingContacts) {
    return null;
  }

  const filtered = navItems.filter((i) => i.id !== 'guards' && i.id !== 'schedule' && i.id !== 'sirens');
  const total = filtered.length;
  const completed = filtered.filter((i) => getSectionState(i.id) === 'complete').length;
  const show = completed < total;

  if (!show) {
    return null;
  }

  const percent = total ? Math.round((completed * 100) / total) : 0;

  const totalDevices = devices.length;
  const hasRelaysOrSpeakers = devices.some(
    (d) => (d.secondaryDevice?.speakers?.length || 0) > 0 || (d.secondaryDevice?.relays?.length || 0) > 0
  );
  const hasAnyGrids = devices.some((d) => {
    const sd = d.secondaryDevice;
    if (!sd) { return false; }
    const type = sd.integrationType;
    const isCamera = type !== 'Speaker' && type !== 'Relay';
    if (!isCamera) { return false; }
    const wkt = sd.activationWkt ?? '';
    return wkt.trim() !== '';
  });
  const hasMissingGrids = hasAnyGrids && devices.some((d) => {
    const sd = d.secondaryDevice;
    if (!sd) { return false; }
    const type = sd.integrationType;
    const isCamera = type !== 'Speaker' && type !== 'Relay';
    if (!isCamera) { return false; }
    const wkt = sd.activationWkt ?? '';
    return wkt.trim() === '';
  });

  return (
    <section className="rounded-lg border bg-[#111] p-6" id="setup-card">
        <h3 className="font-semibold text-xl mb-4">Let’s get your site ready</h3>
      <div className="flex items-center justify-between">
            {show ? (
        <div className="mb-2 flex items-center gap-2 text-sm text-gray-300">
          <InfoIcon className="h-5 w-5" />
          <div>
          
            <p>Complete all the tasks below to improve how your site performs on Watchful</p>{' '}
          </div>
        </div>
      ) : null}
      
      </div>
      {show ? (
        <div className="mb-4 mt-2 h-2 w-full rounded-full bg-gray-800">
          <div className="h-2 rounded-full bg-violet-600" style={{ width: `${percent}%` }} />
        </div>
      ) : null} 
 
      <article className="grid grid-cols-2 gap-4">
        {filtered
          .filter((item) => getSectionState(item.id) !== 'complete')
          .filter((_, i) => i % 2 === 0)
          .map((item) => (
            <a
              key={item.id}
              href={item.href}
              className="flex items-center justify-start rounded-md border p-3 hover:bg-[#1a1a1a]"
            >
              <span className="flex items-center">{renderStatusIcon(getSectionState(item.id))}</span>
              <div className="ml-2 block">
                <p className="font-semibold text-lime">{item.label}</p>
                <p
                  className={`text-sm ${
                    getSectionState(item.id) === 'complete'
                      ? 'text-lime'
                      : getSectionState(item.id) === 'issues'
                      ? 'text-orange-400'
                      : 'text-gray-400'
                  }`}
                >
                  {getSectionMessage(item.id)}
                </p>
              </div>
            </a>
          ))}
        {totalDevices === 0 && (
          <a
            href={`/devices/customer/${companyId}/add-device`}
            className="flex items-center justify-start rounded-md border p-3 hover:bg-[#1a1a1a]"
          >
            <span className="flex items-center">{renderStatusIcon('incomplete')}</span>
            <div className="ml-2 block">
              <p className={`font-semibold text-lime`}>Add a device</p>
              <p className={`text-sm text-gray-400`}>Connect a camera so Watchful can monitor your site.</p>
            </div>
          </a>
        )}
        {totalDevices > 0 && hasMissingGrids && (
          <a
            href={'#devices'}
            className="flex items-center justify-start rounded-md border p-3 hover:bg-[#1a1a1a]"
          >
            <span className="flex items-center">{renderStatusIcon('incomplete')}</span>
            <div className="ml-2 block">
              <p className="font-semibold text-lime">Configure detection grids</p>
              <p className={`text-sm text-gray-400`}>Only activity inside these zones will trigger alerts.</p>
            </div>
          </a>
        )}
        {filtered
          .filter((item) => getSectionState(item.id) !== 'complete')
          .filter((_, i) => i % 2 === 1)
          .map((item) => (
            <a
              key={item.id}
              href={item.href}
              className="flex items-center justify-start rounded-md border p-3 hover:bg-[#1a1a1a]"
            >
              <span className="flex items-center">{renderStatusIcon(getSectionState(item.id))}</span>
              <div className="ml-2 block">
                <p className="font-semibold text-lime">{item.label}</p>
                <p
                  className={`text-sm ${
                    getSectionState(item.id) === 'complete'
                      ? 'text-gray-300'
                      : getSectionState(item.id) === 'issues'
                      ? 'text-orange-400'
                      : 'text-gray-400'
                  }`}
                >
                  {getSectionMessage(item.id)}
                </p>
              </div>
            </a>
          ))}
        {hasRelaysOrSpeakers && (
          <a
            href={'/devices'}
            className="flex items-center justify-start rounded-md border p-3 hover:bg-[#1a1a1a]"
          >
            <span className="flex items-center">{renderStatusIcon('incomplete')}</span>
            <div className="ml-2 block">
              <p className="font-semibold text-lime">Configure component links</p>
              <p className={`text-sm text-gray-400`}>Link speakers and relays to camera feeds</p>
            </div>
          </a>
        )}
      </article>
    </section>
  );
}
